// ── Public Terms of Service / Privacy Policy pages (/terms, /privacy) ───────
// Same look as the landing page (its `.landing-page` light palette, nav,
// deep blue band with the turning facet drawing, dark navy footer). The
// text itself lives once in utils/legalContent.ts, shared with Settings'
// read-only Terms / Privacy tabs.

import { useEffect, useState, type ReactNode } from 'react';
import appLogo from '../assets/icon.png';
import { CONTACT_EMAIL, WHATSAPP_NUMBERS } from '../utils/contact';
import {
  TERMS_SECTIONS, TERMS_LAST_UPDATED, PRIVACY_SECTIONS, PRIVACY_LAST_UPDATED, SUPPORT_EMAIL,
  type LegalSection,
} from '../utils/legalContent';

type Doc = 'terms' | 'privacy';

function IconPhone() {
  return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 1.5h2.2l1.1 3-1.5 1a8 8 0 004.2 4.2l1-1.5 3 1.1v2.2a1.5 1.5 0 01-1.6 1.5A11.5 11.5 0 012 3.1 1.5 1.5 0 013.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>;
}
function IconMail() {
  return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

const DOCS: Record<Doc, { title: string; updated: string; sections: LegalSection[]; intro: string }> = {
  terms: {
    title: 'Terms of Service', updated: TERMS_LAST_UPDATED, sections: TERMS_SECTIONS,
    intro: 'The rules for using DiaWolt. Please read them before you start.',
  },
  privacy: {
    title: 'Privacy Policy', updated: PRIVACY_LAST_UPDATED, sections: PRIVACY_SECTIONS,
    intro: 'What we collect, why, and how your business data stays yours.',
  },
};

const slug = (h: string) => h.toLowerCase().replace(/^\d+\.\s*/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Bold every mention of the support email, like Settings' renderer. */
function withEmail(text: string): ReactNode {
  const parts = text.split(SUPPORT_EMAIL);
  if (parts.length === 1) return text;
  return parts.flatMap((p, i) => i === 0 ? [p] : [<a key={i} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, p]);
}

function LegalPage({ doc }: { doc: Doc }) {
  const d = DOCS[doc];

  // The section being read is highlighted in "On this page".
  const [active, setActive] = useState('');

  useEffect(() => {
    document.title = `${d.title} · DiaWolt`;
    window.scrollTo(0, 0);
  }, [d.title]);

  useEffect(() => {
    const els = d.sections.map(s => document.getElementById(slug(s.heading))).filter((e): e is HTMLElement => !!e);
    const obs = new IntersectionObserver(entries => {
      const hit = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (hit) setActive(hit.target.id);
    }, { rootMargin: '0px 0px -70% 0px' });
    els.forEach(e => obs.observe(e));
    return () => obs.disconnect();
  }, [d.sections]);

  return (
    <div className="landing-page legal-page">
      {/* No top bar: the brand sits centred at the top of the blue band. */}
      <section className="legal-hero">
        <div className="landing-demo-gem" aria-hidden="true" />
        <div className="legal-hero-inner">
          <div className="legal-hero-brand">
            <img src={appLogo} alt="" />
            <span>DiaWolt</span>
          </div>
          <h1>{d.title}</h1>
          <p>{d.intro}</p>
          <div className="legal-hero-updated">Last updated: {d.updated}</div>
        </div>
      </section>

      <main className="legal-body">
        <nav className="legal-toc" aria-label="On this page">
          <div className="legal-toc-title">On this page</div>
          {d.sections.map(s => (
            <a key={s.heading} href={`#${slug(s.heading)}`}
              className={active === slug(s.heading) ? 'active' : undefined}
              onClick={() => setActive(slug(s.heading))}>
              {s.heading}
            </a>
          ))}
        </nav>

        <article className="legal-article">
          {d.sections.map(s => (
            <section key={s.heading} id={slug(s.heading)} className="legal-section">
              <h2>{s.heading}</h2>
              {s.body.map((p, i) => <p key={i}>{withEmail(p)}</p>)}
            </section>
          ))}
          <div className="legal-contact">
            Questions about this page? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </div>
        </article>
      </main>

      {/* Same navy footer as the landing page: brand + tagline, phone
          numbers, email, then the © line. No page links (by request). */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <div className="landing-brand">
              <img src={appLogo} alt="" className="landing-brand-icon landing-brand-icon-sm" />
              <span className="landing-brand-text">DiaWolt</span>
            </div>
            <p className="landing-footer-tagline">Stock, sales and books in one place, made for diamond traders.</p>
          </div>

          <div className="landing-footer-col">
            <div className="landing-footer-heading">Call us</div>
            {WHATSAPP_NUMBERS.map(n => (
              <a key={n.tel} href={`tel:${n.tel}`} className="landing-footer-contact">
                <span className="landing-footer-contact-icon"><IconPhone /></span>
                <span>
                  <span className="landing-footer-contact-label">{n.country}</span>
                  {n.display}
                </span>
              </a>
            ))}
          </div>

          <div className="landing-footer-col">
            <div className="landing-footer-heading">Write to us</div>
            <a href={`mailto:${CONTACT_EMAIL}`} className="landing-footer-contact">
              <span className="landing-footer-contact-icon"><IconMail /></span>
              <span>
                <span className="landing-footer-contact-label">Email</span>
                {CONTACT_EMAIL}
              </span>
            </a>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <span>© {new Date().getFullYear()} DiaWolt. All rights reserved.</span>
          <span>Made for diamond traders</span>
        </div>
      </footer>
    </div>
  );
}

export function TermsPage()   { return <LegalPage doc="terms" />; }
export function PrivacyPage() { return <LegalPage doc="privacy" />; }
